import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TotoPresence from "@/components/toto/TotoPresence";
import PayrollWorkspace from "@/components/toto/PayrollWorkspace";
import { useAuth } from "@/context/AuthContext";
import "@/pages/toto-motion.css";
import "@/components/toto/VesselIdentity.css";
import "@/components/toto/VesselContinuityV2.css";
import "@/components/toto/PayrollWorkspaceOperational.css";
import "@/pages/toto-home-refinement.css";
import "@/pages/toto-environment-glass.css";
import "@/components/toto/TotoGlassIdentity.css";

/**
 * Rainbow Interests · Payroll understanding inside TOTO.
 *
 * SONMI is the governing field. TOTO is Rainbow Interests' Organization
 * Vessel. Payroll is one body of organizational understanding inside TOTO,
 * not a competing shell or a separate top-level vessel.
 *
 * The TOTO artifact remains present above domain work and is the persistent
 * navigation anchor back to SONMI's Atrium.
 * See CANON/ORGANIZATION_VESSEL.md.
 */

// Prefer runtime hostname detection because CRA/CRACO bakes REACT_APP_* values
// when the dev server starts. A long-lived Codespace server can therefore keep
// an older environment even after the repository config changes.
const hostname = typeof window !== "undefined" ? window.location.hostname : "";
const IS_CODESPACE_PREVIEW =
  process.env.REACT_APP_CODESPACE_PREVIEW === "true" ||
  hostname.endsWith(".app.github.dev") ||
  hostname.endsWith(".githubpreview.dev");

const TEAM_IDENTITIES = {
  PLT: {
    name: "Team Green",
    rgb: "92 134 112",
    fieldRgb: "231 240 234",
    fieldGlowRgb: "247 242 231",
    glassRgb: "14 87 56",
    glassDeepRgb: "7 48 31",
    glassHighlightRgb: "37 126 87",
    hex: "#5c8670",
  },
  FLL: {
    name: "Team Blue",
    rgb: "83 119 155",
    fieldRgb: "229 237 244",
    fieldGlowRgb: "244 240 233",
    glassRgb: "15 64 107",
    glassDeepRgb: "8 37 66",
    glassHighlightRgb: "42 108 168",
    hex: "#53779b",
  },
  RNB: {
    name: "Team Rainbow",
    rgb: "86 82 78",
    fieldRgb: "240 238 235",
    fieldGlowRgb: "248 244 238",
    glassRgb: "58 55 52",
    glassDeepRgb: "35 33 31",
    glassHighlightRgb: "112 104 96",
    hex: "#56524e",
  },
};

function PreviewPresence() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 116,
        height: 116,
        display: "block",
        borderRadius: 34,
        background:
          "radial-gradient(circle at 32% 24%, rgba(255,255,255,.96), rgba(255,255,255,.34) 28%, rgba(231,240,234,.34) 58%, rgba(92,134,112,.16) 100%)",
        border: "1px solid rgba(255,255,255,.82)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,.92), inset -10px -12px 24px rgba(92,134,112,.08), 0 18px 42px rgba(54,68,58,.08)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    />
  );
}

export default function PayrollVesselStudy() {
  const { user, logout } = useAuth();
  const [previewBoutique, setPreviewBoutique] = useState(null);
  const navigate = useNavigate();
  // The root route intentionally sends signed-in team members into Payroll
  // during the pilot.  The persistent vessel control, however, must return
  // to SONMI's actual Home field rather than immediately looping back here.
  const returnToSonmiHome = () => navigate("/home");
  const team = TEAM_IDENTITIES[previewBoutique || user?.boutique_code] || TEAM_IDENTITIES.RNB;

  return (
    <main
      className={`toto-home toto-field-study is-field-open has-workspace-open ${IS_CODESPACE_PREVIEW ? "is-codespace-preview" : ""} ${previewBoutique ? "is-boutique-preview" : ""}`}
      style={{
        "--team-rgb": team.rgb,
        "--team-field-rgb": team.fieldRgb,
        "--team-field-glow-rgb": team.fieldGlowRgb,
        "--team-glass-rgb": team.glassRgb,
        "--team-glass-deep-rgb": team.glassDeepRgb,
        "--team-glass-highlight-rgb": team.glassHighlightRgb,
      }}
      data-testid="sonmi-payroll-surface"
      data-preview-mode={IS_CODESPACE_PREVIEW ? "codespace" : "live"}
    >
      <div className="toto-back-glass" aria-hidden="true">
        <span className="toto-back-glass-bloom toto-back-glass-bloom-one" />
        <span className="toto-back-glass-bloom toto-back-glass-bloom-two" />
        <span className="toto-back-glass-grain" />
      </div>

      <div className="toto-liquid-field" aria-hidden="false">
        <div className="toto-liquid-refraction" />
        <div className="toto-liquid-frost" />
      </div>

      <header className="toto-field-header">
        <div className="toto-team-mark">
          <span className="toto-team-dot" aria-hidden="true" />
          {team.name}
        </div>
        <div className="toto-field-controls">
          {user?.design_preview && (
          <button
            type="button"
            className="toto-field-control"
            onClick={() => setPreviewBoutique((current) => current === "FLL" ? null : "FLL")}
            aria-pressed={previewBoutique === "FLL"}
          >
            {previewBoutique === "FLL" ? "Return to Team Green" : "Preview Team Blue"}
          </button>
          )}
          <button type="button" className="toto-field-control" onClick={logout}>Sign out</button>
        </div>
      </header>

      <section
        className="toto-field-arrival"
        aria-label="TOTO · Rainbow Interests organization vessel"
        style={{ zIndex: 90, pointerEvents: "none" }}
      >
        <div className="toto-presence-slot" style={{ pointerEvents: "none" }}>
          <button
            type="button"
            className="toto-contained-artifact"
            onClick={returnToSonmiHome}
            aria-label="Return to SONMI home"
            title="SONMI Home"
            data-testid="toto-artifact-home"
            style={{ pointerEvents: "auto", cursor: "pointer" }}
          >
            {IS_CODESPACE_PREVIEW ? (
              <PreviewPresence />
            ) : (
              <TotoPresence teamColor={team.hex} engaged shellVariant="smooth" />
            )}
          </button>
        </div>
      </section>

      <PayrollWorkspace
        open
        designPreviewBoutique={previewBoutique}
        onOpen={() => {}}
        onClose={returnToSonmiHome}
      />
    </main>
  );
}
